import { playableChatPreviews } from './chatPreviewQueue';
import { normalizeSelectedPairs } from './getStartedSelections';
import { previewTopicKey } from './previewTopic';
import { getOnboardingSelectedSpeakers } from './onboardingSelectedSpeakers';

const STORAGE_KEY = 'podeux_onboarding_player_v1';
const HOME_PICKS_KEY = 'podeux_onboarding_home_picks_v1';
export const ONBOARDING_HOME_PICKS_UPDATED = 'podeux:onboarding-home-picks-updated';
let memorySession = null;
let memoryHomePicks = null;

const positionSeconds = value => Number.isFinite(Number(value)) ? Math.max(0, Number(value)) : 0;

// Playback position is not listening duration: restored positions and seeks
// must never turn into interest signals. Only measured playback is imported.
export const getOnboardingPlayback = (session = readOnboardingSession()) => {
    if (!session || session.guestMigrated) return [];
    const previews = new Map((session.previews || []).map(preview => [preview.id, preview]));
    const completedIds = new Set([
        ...(session.completedPreviewIds || []),
        ...(session.sampleCompleted && session.sample?.id ? [session.sample.id] : []),
    ]);
    const listening = new Map();
    for (const item of Array.isArray(session.onboardingPlayback) ? session.onboardingPlayback : []) {
        const preview = previews.get(item?.previewId);
        if (!preview) continue;
        const duration = positionSeconds(preview.totalTimeSeconds);
        const listenedSeconds = positionSeconds(item.listenedSeconds);
        listening.set(preview.id, Math.max(listening.get(preview.id) || 0,
            duration > 0 ? Math.min(duration, listenedSeconds) : listenedSeconds));
    }
    for (const id of completedIds) {
        if (previews.has(id) && !listening.has(id)) listening.set(id, 0);
    }
    return [...listening].filter(([id, seconds]) => seconds > 0 || completedIds.has(id))
        .map(([previewId, listenedSeconds]) => ({
            previewId, listenedSeconds, completed: completedIds.has(previewId),
        }));
};

// Only explicitly curated, pair-matched clips belong in the guest chooser.
// The response also includes a general discovery feed for the eventual account.
export const getOnboardingStarterHighlights = ({
    starterHighlights, previews = [], selectedPairs = [], rosterVersion, sample,
} = {}) => {
    const pairs = normalizeSelectedPairs(selectedPairs);
    const candidates = Array.isArray(starterHighlights) ? starterHighlights : previews;
    const matched = playableChatPreviews(candidates).filter(preview =>
        Number.isFinite(preview.starterScore)
        && preview.starterTopic
        && (!rosterVersion || !preview.starterRosterVersion || preview.starterRosterVersion === rosterVersion)
        && pairs.some(pair => pair.speakerId === preview.person?.id
            && previewTopicKey(pair.topic) === previewTopicKey(preview.starterTopic)));
    // Browsing must preserve the ranked order. Older sessions without pair
    // metadata can still replay their exact sample as a fallback.
    return playableChatPreviews([
        ...(sample && !matched.some(preview => preview.id === sample.id) ? [sample] : []),
        ...matched,
    ]).slice(0, 3);
};

// Separate from router state: PreviewCard replaces the browser URL, and a
// refresh must retain the same sample, playback position, and signup purpose.
export const readOnboardingSession = () => {
    let session;
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        try {
            session = raw ? JSON.parse(raw) : null;
        } catch {
            persistSession(null);
            return null;
        }
    } catch {
        session = memorySession;
    }
    if (!session?.newId || !Array.isArray(session.previews)) return null;
    const previews = playableChatPreviews(session.previews);
    const sample = previews.find(preview => preview.id === session.sample?.id);
    const position = positionSeconds(session.position);
    return sample ? {
        ...session, sample, previews, position,
        starterHighlights: getOnboardingStarterHighlights({ ...session, previews, sample }),
        heardPreviewIds: [...new Set([
            ...(Array.isArray(session.heardPreviewIds) ? session.heardPreviewIds : []),
            ...(session.sampleStarted || position > 0 ? [sample.id] : []),
        ])],
        completedPreviewIds: [...new Set([
            ...(Array.isArray(session.completedPreviewIds) ? session.completedPreviewIds : []),
            ...(session.sampleCompleted ? [sample.id] : []),
        ])],
        sampleStarted: Boolean(session.sampleStarted) || position > 0,
    } : null;
};

const persistSession = session => {
    memorySession = session;
    try {
        if (session) window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
        else window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
        // The in-memory session still works when browser storage is unavailable.
    }
    return session;
};

export const startOnboardingSession = ({
    newId, previews: candidates = [], starterHighlights, sampleId, rosterVersion, selectedPairs = [],
    selectedSpeakerIds = [], selectedSpeakers = [],
} = {}) => {
    const previews = playableChatPreviews([...candidates, ...(Array.isArray(starterHighlights) ? starterHighlights : [])]);
    const sample = previews.find(preview => preview.id === sampleId) || previews[0];
    if (!newId || !sample) return persistSession(null);
    return persistSession({
        newId,
        rosterVersion: typeof rosterVersion === 'string' ? rosterVersion : null,
        selectedPairs: normalizeSelectedPairs(selectedPairs),
        selectedSpeakerIds,
        selectedSpeakers: getOnboardingSelectedSpeakers({ selectedPairs, selectedSpeakerIds, selectedSpeakers, previews }),
        sample,
        previews,
        starterHighlights: getOnboardingStarterHighlights({ starterHighlights, previews, selectedPairs, rosterVersion, sample }),
        phase: 'ready',
        reason: null,
        action: null,
        position: 0,
        signedIn: false,
        guestMigrated: false,
        sampleCompleted: false,
        sampleStarted: false,
        playIntroShown: false,
        heardPreviewIds: [],
        completedPreviewIds: [],
        onboardingPlayback: [],
    });
};

export const updateOnboardingSession = (patch, { newId, previewId } = {}) => {
    const session = readOnboardingSession();
    if ((newId && session?.newId !== newId) || (previewId && session?.sample.id !== previewId)) return null;
    return session ? persistSession({ ...session, ...patch, position: positionSeconds(patch.position ?? session.position) }) : null;
};

export const selectOnboardingHighlight = previewId => {
    const session = readOnboardingSession();
    const sample = session?.starterHighlights.find(preview => preview.id === previewId);
    if (!sample) return null;
    return updateOnboardingSession({
        sample, phase: 'ready', position: 0, reason: null, action: null,
        sampleStarted: false, sampleCompleted: false, playIntroShown: true,
    });
};

export const recordOnboardingPlayback = (identity = {}) => {
    const session = readOnboardingSession();
    return session ? updateOnboardingSession({
        sampleStarted: true,
        heardPreviewIds: [...new Set([...session.heardPreviewIds, session.sample.id])],
    }, identity) : null;
};

export const recordOnboardingListening = ({ previewId, listenedSeconds, duration, newId } = {}) => {
    const session = readOnboardingSession();
    const preview = session?.previews.find(item => item.id === previewId);
    if (!preview || session.guestMigrated || session.signedIn
        || (newId && newId !== session.newId) || positionSeconds(listenedSeconds) === 0) return session;
    const playback = getOnboardingPlayback(session);
    const previous = playback.find(item => item.previewId === previewId);
    const limits = [duration, preview.totalTimeSeconds].map(positionSeconds).filter(value => value > 0);
    const total = (previous?.listenedSeconds || 0) + positionSeconds(listenedSeconds);
    return updateOnboardingSession({
        onboardingPlayback: [
            ...playback.filter(item => item.previewId !== previewId),
            {
                previewId,
                listenedSeconds: limits.length ? Math.min(total, ...limits) : total,
                completed: Boolean(previous?.completed),
            },
        ],
        heardPreviewIds: [...new Set([...session.heardPreviewIds, previewId])],
    });
};

export const finishOnboardingHighlight = position => {
    const session = readOnboardingSession();
    return session ? updateOnboardingSession({
        phase: 'complete', position, sampleCompleted: true,
        heardPreviewIds: [...new Set([...session.heardPreviewIds, session.sample.id])],
        completedPreviewIds: [...new Set([...session.completedPreviewIds, session.sample.id])],
    }) : null;
};

export const requestOnboardingSignup = ({ reason = 'action', action = null, position = 0 } = {}) =>
    updateOnboardingSession({
        phase: 'signup',
        reason,
        action,
        position,
        sampleCompleted: (reason === 'complete' && action === null)
            || Boolean(readOnboardingSession()?.sampleCompleted),
    });

export const markOnboardingSignedIn = () => updateOnboardingSession({
    phase: 'preview',
    signedIn: true,
    guestMigrated: true,
    savePending: false,
});

export const clearOnboardingSession = () => persistSession(null);
export const beginOnboardingSession = startOnboardingSession;
export const completeOnboardingSession = clearOnboardingSession;
export const getOnboardingPreviewPath = session => session?.sample
    ? `/preview/${session.sample.fullTrackId}/${session.sample.messageIndex}` : null;

export const getUpcomingOnboardingHighlights = (previews = [], sampleId, heardPreviewIds = []) => {
    const playable = playableChatPreviews(previews);
    const excluded = new Set([sampleId || playable[0]?.id, ...heardPreviewIds]);
    return playable.filter(preview => !excluded.has(preview.id)).slice(0, 2);
};

const persistHomePicks = picks => {
    memoryHomePicks = picks;
    try {
        if (picks) window.sessionStorage.setItem(HOME_PICKS_KEY, JSON.stringify(picks));
        else window.sessionStorage.removeItem(HOME_PICKS_KEY);
    } catch {
        // Preserve the handoff for this tab when storage is unavailable.
    }
    return picks;
};

// Called only after the account request succeeds. This separate handoff remains
// available after Home clears the guest player session or an action returns to it.
export const saveOnboardingHomePicks = ({ uid, previews = [], rosterVersion, selectedPairs = [] } = {}) => {
    const playable = playableChatPreviews(previews).slice(0, 2);
    const picks = typeof uid === 'string' && uid.trim() && playable.length
        ? persistHomePicks({
            uid, previews: playable,
            rosterVersion: typeof rosterVersion === 'string' ? rosterVersion : null,
            selectedPairs: normalizeSelectedPairs(selectedPairs),
        }) : persistHomePicks(null);
    // Redux can publish backend sign-in before this local handoff is saved.
    // Refresh an already-visible Home for the same account in this tab.
    if (typeof uid === 'string' && uid.trim()) {
        window.dispatchEvent(new CustomEvent(ONBOARDING_HOME_PICKS_UPDATED, { detail: { uid } }));
    }
    return picks;
};

export const clearOnboardingHomePicks = () => persistHomePicks(null);

export const readOnboardingHomePicks = uid => {
    if (!uid) return [];
    let picks;
    try {
        const raw = window.sessionStorage.getItem(HOME_PICKS_KEY);
        try {
            picks = raw ? JSON.parse(raw) : null;
        } catch {
            clearOnboardingHomePicks();
            return [];
        }
    } catch {
        picks = memoryHomePicks;
    }
    if (!picks) return [];
    if (picks.uid !== uid || !Array.isArray(picks.previews)) {
        clearOnboardingHomePicks();
        return [];
    }
    return playableChatPreviews(picks.previews).slice(0, 2);
};
